// /contacts — Outreach helper home page (Session 7 PR3).
//
// Per-company grouping of the user's contact book. Each card surfaces the
// contact's identity, the last-contacted age, the link count to applications,
// and a "Draft message" affordance. Add Contact lives in the page header.
//
// Empty state nudges first-time users to add their first contact (the
// outreach loop is dormant until at least one mark-sent has fed the
// artifacts loop).

import { useMemo, useState } from "react";
import { Mail, MessageSquare, UserPlus } from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Pill } from "../components/ui/Pill";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { useContacts, type ContactRow } from "../lib/contacts";
import { AddContactDialog } from "../components/contacts/AddContactDialog";
import { MessageDraftDialog } from "../components/contacts/MessageDraftDialog";
import { EditContactSheet } from "../components/contacts/EditContactSheet";

export default function Contacts() {
  const q = useContacts();
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<ContactRow | null>(null);

  const groups = useMemo(() => groupByCompany(q.data?.contacts ?? []), [q.data]);

  return (
    <section className="space-y-4 p-4 sm:p-6 lg:p-8">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-100">Outreach</h1>
          <p className="mt-1 text-xs text-neutral-500">
            Recruiters, hiring managers, and references you've talked to.
            Drafts pull from your career corpus and past messages of the
            same kind — the more you mark sent, the more it sounds like you.
          </p>
        </div>
        <AddContactDialog
          trigger={
            <Button variant="primary" leadingIcon={<UserPlus size={14} />}>
              Add contact
            </Button>
          }
        />
      </header>

      {q.isLoading ? (
        <ul className="grid gap-3 lg:grid-cols-2">
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <Skeleton className="h-32 w-full rounded-lg" />
            </li>
          ))}
        </ul>
      ) : q.error ? (
        <EmptyState
          title="Couldn't load contacts"
          description={q.error instanceof Error ? q.error.message : "Try again."}
        />
      ) : (q.data?.contacts ?? []).length === 0 ? (
        <EmptyState
          title="No contacts yet"
          description="Add a recruiter or hiring manager you've talked to. Drafts get tailored to them and to the application they're attached to."
          action={
            <AddContactDialog
              trigger={<Button variant="primary">Add your first contact</Button>}
            />
          }
        />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.key} className="space-y-2">
              <h2 className="text-xs uppercase tracking-wider text-neutral-500">
                {group.label}{" "}
                <span className="text-neutral-700">· {group.contacts.length}</span>
              </h2>
              <ul className="grid gap-3 lg:grid-cols-2">
                {group.contacts.map((c) => (
                  <li key={c.id}>
                    <ContactCard
                      row={c}
                      onEdit={() => {
                        setEditRow(c);
                        setEditOpen(true);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <EditContactSheet open={editOpen} onOpenChange={setEditOpen} row={editRow} />
    </section>
  );
}

function ContactCard({ row, onEdit }: { row: ContactRow; onEdit: () => void }) {
  const lastContacted =
    row.last_contacted_at !== null ? formatAge(row.last_contacted_at) : null;

  return (
    <Card>
      <CardHeader
        title={
          <button
            type="button"
            onClick={onEdit}
            className="text-left text-sm font-semibold text-neutral-100 hover:text-accent-300"
          >
            {row.full_name}
          </button>
        }
        description={
          row.role
            ? `${row.role}${row.company_name ? ` · ${row.company_name}` : ""}`
            : row.company_name ?? "Unaffiliated"
        }
        action={
          <div className="flex items-center gap-1">
            {row.link_count > 0 ? (
              <Pill tone="info">{row.link_count} app</Pill>
            ) : null}
            {lastContacted ? (
              <Pill tone="neutral">last: {lastContacted}</Pill>
            ) : (
              <Pill tone="warning">never contacted</Pill>
            )}
          </div>
        }
      />
      <CardBody className="flex items-center justify-between gap-2 text-xs text-neutral-400">
        <div className="flex items-center gap-2 truncate">
          {row.email ? (
            <span className="inline-flex items-center gap-1 truncate">
              <Mail size={11} aria-hidden /> <span className="truncate">{row.email}</span>
            </span>
          ) : null}
          {row.linkedin_url ? (
            <a
              className="text-accent-300 hover:text-accent-200"
              href={row.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
          ) : null}
        </div>
        <MessageDraftDialog
          contact={row}
          trigger={
            <Button variant="secondary" size="sm" leadingIcon={<MessageSquare size={12} />}>
              Draft
            </Button>
          }
        />
      </CardBody>
    </Card>
  );
}

interface ContactGroup {
  key: string;
  label: string;
  contacts: ContactRow[];
}

function groupByCompany(rows: ContactRow[]): ContactGroup[] {
  const byKey = new Map<string, ContactGroup>();
  for (const row of rows) {
    const key = row.company_id ?? "__other__";
    const label = row.company_name ?? "Other / unknown";
    let group = byKey.get(key);
    if (!group) {
      group = { key, label, contacts: [] };
      byKey.set(key, group);
    }
    group.contacts.push(row);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    // "Other" group last; otherwise by label.
    if (a.key === "__other__") return 1;
    if (b.key === "__other__") return -1;
    return a.label.localeCompare(b.label);
  });
}

function formatAge(at: number): string {
  const ms = Date.now() - at;
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(ms / day);
  if (days < 1) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
